import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  AlertTriangle,
  DollarSign,
  BarChart3,
  Target,
  Clock,
  X,
  Check,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// PAPER TRADE TABLE - LAZY LOADING
// ============================================
// This component only fetches data when mounted (tab opened)
// It does NOT receive data via WebSocket to ensure zero latency impact

const PaperTradeTable = () => {
  const [trades, setTrades] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ symbol: '', status: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [forceClosing, setForceClosing] = useState(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  // Fetch all data when component mounts
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      
      // Check if we have a token
      if (!headers.Authorization || headers.Authorization === 'Bearer ') {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      const [statsRes, openRes, historyRes, summaryRes] = await Promise.all([
        fetch(`${API}/paper-trade/stats`, { headers }),
        fetch(`${API}/paper-trade/open`, { headers }),
        fetch(`${API}/paper-trade/history?limit=100`, { headers }),
        fetch(`${API}/paper-trade/daily-summary`, { headers }),
      ]);

      // Check for auth errors (401/403 return HTML)
      const checkResponse = async (res, name) => {
        if (!res.ok) {
          const text = await res.text();
          if (text.startsWith('<') || text.includes('<!DOCTYPE')) {
            throw new Error(`${name}: Authentication required`);
          }
          try {
            const err = JSON.parse(text);
            throw new Error(`${name}: ${err.detail || err.message || 'Failed'}`);
          } catch {
            throw new Error(`${name}: HTTP ${res.status}`);
          }
        }
        return res.json();
      };

      const [statsData, openData, historyData, summaryData] = await Promise.all([
        checkResponse(statsRes, 'Stats'),
        checkResponse(openRes, 'Open trades'),
        checkResponse(historyRes, 'History'),
        checkResponse(summaryRes, 'Summary'),
      ]);

      setStats(statsData);
      setOpenTrades(openData.open_trades || []);
      setTrades(historyData.trades || []);
      setDailySummary(summaryData);
    } catch (err) {
      console.error('Error fetching paper trade data:', err);
      setError(err.message);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Force close a trade
  const handleForceClose = async (tradeId) => {
    if (!window.confirm('Are you sure you want to force close this trade at current market price?')) {
      return;
    }

    setForceClosing(tradeId);
    try {
      const res = await fetch(`${API}/paper-trade/force-close/${tradeId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      
      if (data.success) {
        alert(`✅ Trade closed at ${data.exit_price}`);
        fetchData(); // Refresh data
      } else {
        alert(`❌ Failed: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    }
    setForceClosing(null);
  };

  // Filter trades
  const filteredTrades = trades.filter(trade => {
    if (filter.symbol && trade.symbol !== filter.symbol) return false;
    if (filter.status && trade.status !== filter.status) return false;
    return true;
  });

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: 'short'
    });
  };

  // Format P&L with color
  const formatPnL = (pnl, points = false) => {
    if (pnl === null || pnl === undefined) return '-';
    const formatted = points 
      ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} pts`
      : `₹${pnl >= 0 ? '+' : ''}${pnl.toFixed(0)}`;
    return (
      <span className={pnl >= 0 ? 'text-buy' : 'text-sell'}>
        {formatted}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="paper-trade-loading">
        <RefreshCw className="w-8 h-8 text-accent animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading paper trades...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="paper-trade-table">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-accent" />
            Paper Trade Analytics
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Zero-latency paper trading with background logging
          </p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-paper-trades-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="panel p-4 bg-sell/10 border-sell/30 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <div>
            <p className="text-sm font-medium text-sell">Error loading data</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="panel p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Session P&L
            </div>
            <p className={`text-2xl font-bold font-mono ${stats.session_pnl >= 0 ? 'text-buy' : 'text-sell'}`}>
              ₹{stats.session_pnl?.toLocaleString() || 0}
            </p>
          </div>
          
          <div className="panel p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Target className="w-4 h-4" />
              Win Rate
            </div>
            <p className="text-2xl font-bold font-mono text-foreground">
              {stats.win_rate || 0}%
            </p>
            <p className="text-xs text-muted-foreground">
              {stats.session_wins || 0}W / {stats.session_losses || 0}L
            </p>
          </div>
          
          <div className="panel p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <BarChart3 className="w-4 h-4" />
              Total Trades
            </div>
            <p className="text-2xl font-bold font-mono text-foreground">
              {stats.session_trades || 0}
            </p>
          </div>
          
          <div className="panel p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Clock className="w-4 h-4" />
              Open Trades
            </div>
            <p className="text-2xl font-bold font-mono text-accent">
              {stats.open_trades || 0}
            </p>
          </div>
        </div>
      )}

      {/* Daily Summary */}
      {dailySummary && dailySummary.total_trades > 0 && (
        <div className="panel p-4">
          <h3 className="font-semibold text-foreground mb-3">Today's Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Date:</span>
              <span className="ml-2 font-mono">{dailySummary.date}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Total P&L:</span>
              <span className={`ml-2 font-mono ${dailySummary.total_pnl >= 0 ? 'text-buy' : 'text-sell'}`}>
                ₹{dailySummary.total_pnl?.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Trades:</span>
              <span className="ml-2 font-mono">{dailySummary.total_trades}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Win Rate:</span>
              <span className="ml-2 font-mono">{dailySummary.total_win_rate}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Open Trades Section */}
      {openTrades.length > 0 && (
        <div className="panel" data-testid="open-trades-section">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-4 h-4 text-warning" />
              Open Positions ({openTrades.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-highlight">
                <tr>
                  <th className="text-left p-3 font-medium text-muted-foreground">Symbol</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Direction</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Entry <span className="text-cyan-400 text-xs">(Futures)</span></th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Qty</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Signal</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Time</th>
                  <th className="text-center p-3 font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {openTrades.map((trade) => (
                  <tr key={trade.trade_id} className="hover:bg-surface-highlight/50">
                    <td className="p-3 font-mono font-semibold">{trade.symbol}</td>
                    <td className="p-3">
                      <Badge className={trade.direction === 'LONG' 
                        ? 'bg-buy/20 text-buy border-buy/50' 
                        : 'bg-sell/20 text-sell border-sell/50'
                      }>
                        {trade.direction === 'LONG' ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : (
                          <TrendingDown className="w-3 h-3 mr-1" />
                        )}
                        {trade.direction}
                      </Badge>
                    </td>
                    <td className="p-3 text-right font-mono">{trade.entry_price?.toFixed(2)}</td>
                    <td className="p-3 text-right font-mono">{trade.quantity}</td>
                    <td className="p-3 text-xs text-muted-foreground">{trade.trigger_signal || '-'}</td>
                    <td className="p-3 text-xs text-muted-foreground">{formatTime(trade.entry_time)}</td>
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-sell/10 border-sell/30 text-sell hover:bg-sell/20"
                        onClick={() => handleForceClose(trade.trade_id)}
                        disabled={forceClosing === trade.trade_id}
                        data-testid={`force-close-${trade.trade_id}`}
                      >
                        {forceClosing === trade.trade_id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        Close
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trade History Table */}
      <div className="panel" data-testid="trade-history-section">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Trade History</h3>
          
          {/* Filter Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="text-muted-foreground"
          >
            Filters
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        
        {/* Filters */}
        {showFilters && (
          <div className="p-4 border-b border-border bg-surface-highlight flex gap-4">
            <select
              className="bg-surface border border-border rounded px-3 py-1 text-sm"
              value={filter.symbol}
              onChange={(e) => setFilter(f => ({ ...f, symbol: e.target.value }))}
            >
              <option value="">All Symbols</option>
              <option value="NIFTY">NIFTY</option>
              <option value="BANKNIFTY">BANKNIFTY</option>
            </select>
            <select
              className="bg-surface border border-border rounded px-3 py-1 text-sm"
              value={filter.status}
              onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="OPEN">Open</option>
              <option value="CLOSED">Closed</option>
              <option value="FORCE_CLOSED">Force Closed</option>
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilter({ symbol: '', status: '' })}
            >
              Clear
            </Button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-highlight">
              <tr>
                <th className="text-left p-3 font-medium text-muted-foreground">Symbol</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Direction</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Entry <span className="text-cyan-400 text-xs">(Futures)</span></th>
                <th className="text-right p-3 font-medium text-muted-foreground">Exit <span className="text-cyan-400 text-xs">(Futures)</span></th>
                <th className="text-right p-3 font-medium text-muted-foreground">P&L (pts)</th>
                <th className="text-right p-3 font-medium text-muted-foreground">P&L (₹)</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Entry Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredTrades.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground">
                    No trades found
                  </td>
                </tr>
              ) : (
                filteredTrades.map((trade) => (
                  <tr key={trade.trade_id} className="hover:bg-surface-highlight/50">
                    <td className="p-3 font-mono font-semibold">{trade.symbol}</td>
                    <td className="p-3">
                      <Badge className={trade.direction === 'LONG' 
                        ? 'bg-buy/20 text-buy border-buy/50' 
                        : 'bg-sell/20 text-sell border-sell/50'
                      }>
                        {trade.direction}
                      </Badge>
                    </td>
                    <td className="p-3 text-right font-mono">{trade.entry_price?.toFixed(2)}</td>
                    <td className="p-3 text-right font-mono">{trade.exit_price?.toFixed(2) || '-'}</td>
                    <td className="p-3 text-right font-mono">{formatPnL(trade.pnl_points, true)}</td>
                    <td className="p-3 text-right font-mono">{formatPnL(trade.pnl)}</td>
                    <td className="p-3">
                      <Badge className={
                        trade.status === 'OPEN' 
                          ? 'bg-warning/20 text-warning border-warning/50'
                          : trade.status === 'CLOSED'
                          ? 'bg-buy/20 text-buy border-buy/50'
                          : 'bg-muted/20 text-muted-foreground border-border'
                      }>
                        {trade.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{formatTime(trade.entry_time)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {filteredTrades.length > 0 && (
          <div className="p-3 border-t border-border text-center text-xs text-muted-foreground">
            Showing {filteredTrades.length} of {trades.length} trades
          </div>
        )}
      </div>

      {/* Worker Stats */}
      {stats && (
        <div className="text-xs text-muted-foreground text-center">
          Queue: {stats.queue_size} pending | Worker: {stats.worker_trades_logged} logged, {stats.worker_errors} errors
        </div>
      )}
    </div>
  );
};

export default PaperTradeTable;
